import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { 
  Save, Trash2, ArrowLeft, Filter, Plus, ChevronLeft, ChevronRight, Paperclip, 
  Settings, Activity, Info, Type, Minus
} from 'lucide-react';
import api from './axiosConfig';
import toast from 'react-hot-toast';
import Swal from 'sweetalert2';
import { useTranslation } from 'react-i18next'; // 🎯 الترجمة

// استيراد السياقات الموحدة
import AttachmentsModal from './AttachmentsModal';
import { usePermissions } from './usePermissions';
import { useFontSize } from './FontSizeContext';

const CodeTable = ({ title, items, type, updateRow, addNewRow, deleteRow, t, isRTL, readOnly }) => (
  <div className="bg-white border rounded shadow-sm mb-6 overflow-hidden">
    <div className={`bg-gray-50 p-2 border-b flex justify-between items-center text-[10px] font-black uppercase text-gray-500 tracking-widest px-4 `}>
      <div className={`flex items-center gap-2 `}><Filter size={12}/> {title}</div>
      <span>{items?.length || 0} {t('common.records', 'Records')}</span>
    </div>
    <table className={`w-full text-[11px] ${isRTL ? 'text-right' : 'text-left'}`}>
      <thead className="bg-white border-b text-gray-400 font-bold uppercase">
        <tr>
          <th className={`p-2 w-1/4 px-4 font-black ${isRTL ? 'border-l' : 'border-r'}`}>{t('failure_codes.fields.code', 'Code')}</th>
          <th className="p-2 px-4 font-black">{t('common.description', 'Description')}</th>
          <th className="w-10"></th>
        </tr>
      </thead>
      <tbody>
        {items?.map((item, idx) => (
          <tr key={`${type}-${idx}`} className="border-b hover:bg-blue-50/50 transition-colors">
            <td className={`p-1 px-4 ${isRTL ? 'border-l' : 'border-r'}`}>
              <input 
                className={`w-full bg-transparent outline-none font-black text-blue-900 uppercase placeholder:text-gray-300 ${isRTL ? 'text-right' : 'text-left'}`} 
                value={item.code} 
                onChange={e => updateRow(type, idx, 'code', e.target.value)} 
                placeholder={t('failure_codes.placeholders.code', 'Enter Code...')} 
                disabled={readOnly}
              />
            </td>
            <td className="p-1 px-4">
              <input 
                className={`w-full bg-transparent outline-none font-bold text-gray-600 placeholder:text-gray-200 ${isRTL ? 'text-right' : 'text-left'}`} 
                value={item.description} 
                onChange={e => updateRow(type, idx, 'description', e.target.value)} 
                placeholder={t('failure_codes.placeholders.desc', 'Technical Description...')} 
                disabled={readOnly}
              />
            </td>
            <td className="p-1 text-center">
              {!readOnly && (
                <Trash2 
                  size={14} 
                  className="text-gray-300 hover:text-red-500 cursor-pointer mx-auto transition-colors" 
                  onClick={() => deleteRow(type, idx)}
                />
              )}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
    {!readOnly && (
      <div className={`p-2 ${isRTL ? 'text-right' : 'text-left'}`}>
        <button 
          type="button"
          onClick={() => addNewRow(type)} 
          className="m-1 bg-[#3c70a4] text-white px-4 py-1.5 rounded-sm font-black text-[9px] uppercase shadow-sm hover:bg-[#1a3353] transition-all flex items-center gap-1 w-fit"
        >
          <Plus size={12}/> {t('common.new_row', 'NEW ROW')}
        </button>
      </div>
    )}
  </div>
);

const FailureCodesDetails = () => {
  const { id } = useParams();
  const isNew = !id ||  id === 'new';
  const navigate = useNavigate();
  const locationPath = useLocation();
  
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language.startsWith('ar') || i18n.language.startsWith('ur');
  const { canCreate, canUpdate, canDelete } = usePermissions('failure_codes');
  const fontContext = useFontSize();
  const fontSize = fontContext?.fontSize || 13;
  
  const [failureClass, setFailureClass] = useState({
    failure_class: '', description: '', org_id: '', problems: [], causes: [], remedies: []
  });

  const [loading, setLoading] = useState(false);
  const [nav, setNav] = useState({ first: null, last: null, next: null, prev: null, total: 0, current_pos: 0 });
  const [showAttachments, setShowAttachments] = useState(false);
  const [attachCount, setAttachCount] = useState(0);
  
  const user = JSON.parse(localStorage.getItem('user'));
  const userOrg = user?.org_id || '';

  // --- مكون التحكم في الخط ---
  const FontControls = () => (
    <div className="flex items-center gap-2 bg-white/10 px-3 py-1 rounded-full border border-white/20 ml-4">
      <button onClick={() => fontContext.changeFontSize(-1)} className="hover:text-orange-400 transition-colors"><Minus size={14} /></button>
      <div className="flex items-center gap-1 min-w-[40px] justify-center text-blue-200">
        <Type size={14} /><span className="font-black text-[10px]">{fontSize}</span>
      </div>
      <button onClick={() => fontContext.changeFontSize(1)} className="hover:text-orange-400 transition-colors"><Plus size={14} /></button>
    </div>
  );

  const loadData = useCallback(async () => {
    if (isNew) {
        setFailureClass(prev => ({ ...prev, org_id: userOrg }));
        return;
    }
    setLoading(true);
    try {
      const res = await api.get(`/failure-classes/${id}`);
      const d = res.data;
      setFailureClass({
        ...d,
        org_id: d.org_id || userOrg,
        problems: d.problems || [], causes: d.causes || [], remedies: d.remedies || []
      });
      fetchAttachCount(d.failure_class);
    } catch (err) {
      toast.error(t('common.load_error', "Error loading library data"));
    } finally {
      setLoading(false);
    }
  }, [id, isNew, userOrg, t]);

  const loadNavigation = useCallback(async () => {
    if (isNew) return;
    try {
      const res = await api.get(`/failure-classes/${id}/navigation${locationPath.search}`);
      setNav(res.data);
    } catch (err) { console.error("Nav failed"); }
  }, [id, isNew, locationPath.search]);

  const fetchAttachCount = async (fCode) => {
    try {
      const res = await api.get(`/attachments/count/FAILURE/${fCode}`);
      setAttachCount(res.data.count || 0);
    } catch (err) { console.error(err); }
  };

  useEffect(() => { loadData(); loadNavigation(); }, [loadData, loadNavigation]);

  const goTo = (targetId) => {
    if (targetId) navigate(`/failure-codes/${targetId}${locationPath.search}`);
  };

  const updateRow = (type, idx, field, val) => {
    const listKey = type.toLowerCase() + 's';
    setFailureClass(prev => {
      const updatedList = [...prev[listKey]];
      updatedList[idx] = { ...updatedList[idx], [field]: val };
      return { ...prev, [listKey]: updatedList };
    });
  };

  const addNewRow = (type) => {
    const listKey = type.toLowerCase() + 's';
    const newRow = { code: '', description: '', type: type.toUpperCase() };
    setFailureClass(prev => ({ ...prev, [listKey]: [...(prev[listKey] || []), newRow] }));
  };

  const deleteRow = (type, idx) => {
    const listKey = type.toLowerCase() + 's';
    setFailureClass(prev => ({
      ...prev, [listKey]: prev[listKey].filter((_, i) => i !== idx)
    }));
  };

  const handleSave = async () => {
    if (!failureClass.failure_class) return toast.error(t('failure_codes.messages.req_class', "Failure Class Code is required!"));
    
    const payload = {
      ...failureClass,
      all_codes: [
        ...(failureClass.problems || []), 
        ...(failureClass.causes || []), 
        ...(failureClass.remedies || [])
      ]
    };
    
    const lid = toast.loading(t('common.saving', "Syncing Failure Library..."));
    try {
      const res = await api.post('/failure-classes/save', payload);
      toast.success(t('common.save_success', "Library Updated"), { id: lid });
      if (isNew) navigate(`/failure-codes/${res.data.id}${locationPath.search}`);
    } catch (err) { toast.error(t('common.save_fail', "Save Failed"), { id: lid }); }
  };

  const handleDelete = async () => {
    const result = await Swal.fire({
      title: t('common.are_you_sure', 'Are you sure?'),
      text: t('failure_codes.messages.delete_warn', { class: failureClass.failure_class }, `You are about to delete Failure Class ${failureClass.failure_class}.`),
      icon: 'warning', showCancelButton: true, confirmButtonColor: '#d33', cancelButtonColor: '#3085d6',
      confirmButtonText: t('common.yes_delete', 'Yes, Delete it!'),
      cancelButtonText: t('common.cancel', 'Cancel'),
      customClass: { popup: `${isRTL ? 'font-cairo' : 'font-sans'} text-[12px]` }
    });

    if (result.isConfirmed) {
      const loadId = toast.loading(t('common.deleting', "Deleting record..."));
      try {
        await api.delete(`/failure-classes/${id}`);
        toast.success(t('common.delete_success', "Record Deleted Successfully"), { id: loadId });
        navigate(`/failure-codes${locationPath.search}`); 
      } catch (err) { toast.error(t('common.delete_fail', "Delete operation failed"), { id: loadId }); }
    }
  };

  const isReadOnly = !isNew && !canUpdate;

  if (loading) return <div className="p-20 text-center animate-pulse font-black text-[#005495] uppercase tracking-[0.3em]">{t('common.loading', 'Loading Context...')}</div>;

  return (
    <div className={`flex flex-col h-screen bg-[#f3f4f6] uppercase tracking-tighter duration-300 transition-[font-size] ${isRTL ? 'font-cairo text-right' : 'font-sans text-left'}`} style={{ fontSize: `${fontSize}px` }} dir={isRTL ? 'rtl' : 'ltr'}>
      
      {/* 🎯 Toolbar الاحترافي */}
      <div className="bg-[#1a3353] text-white p-1.5 flex justify-between items-center shadow-lg z-30 px-4 shrink-0">
        <div className={`flex items-center gap-3 ${isRTL ? 'mr-2' : 'ml-2'}`}>
          <button onClick={() => navigate(`/failure-codes${locationPath.search}`)} className="p-1.5 hover:bg-white/10 rounded-full transition-all text-blue-200">
            {isRTL ? <ChevronRight size={18} /> : <ArrowLeft size={18} />}
          </button>
          
          {/* Navigation Controls */}
          {!isNew && (
            <div className={`flex items-center bg-black/20 border border-white/10 rounded-md px-1 py-0.5 gap-1 `}>
              <button disabled={!nav.prev} onClick={() => goTo(nav.first)} className="p-1 hover:text-orange-400 disabled:opacity-30"><ChevronLeft size={16} strokeWidth={3} className={isRTL ? 'rotate-180' : ''}/></button>
              <button disabled={!nav.prev} onClick={() => goTo(nav.prev)} className="p-1 hover:text-orange-400 disabled:opacity-30"><ChevronLeft size={16} className={isRTL ? 'rotate-180' : ''}/></button>
              <div className="px-3 text-[0.9em] font-mono border-x border-white/10 mx-1">
                <span className="text-orange-400 font-black">{nav.current_pos}</span> / {nav.total}
              </div>
              <button disabled={!nav.next} onClick={() => goTo(nav.next)} className="p-1 hover:text-orange-400 disabled:opacity-30"><ChevronRight size={16} className={isRTL ? 'rotate-180' : ''}/></button>
              <button disabled={!nav.next} onClick={() => goTo(nav.last)} className="p-1 hover:text-orange-400 disabled:opacity-30"><ChevronRight size={16} strokeWidth={3} className={isRTL ? 'rotate-180' : ''}/></button>
            </div>
          )}

          <div className="flex flex-col ml-4">
              <span className="text-[0.9em] text-orange-400 font-bold uppercase tracking-widest">{t('failure_codes.details.profile', 'Failure Class')}</span>
              <h1 className="text-sm font-black tracking-tighter">{failureClass.failure_class || t('common.new', 'NEW RECORD')}</h1>
          </div>
        </div>

        <div className={`flex items-center gap-2 ${isRTL ? 'ml-2' : 'mr-2'}`}>
          {((isNew && canCreate) || (!isNew && canUpdate)) && (
            <button onClick={handleSave} className="bg-green-600 hover:bg-green-700 px-4 py-1.5 rounded-sm font-black text-[1.0em] flex items-center gap-2 uppercase transition-all shadow-md">
              <Save size={14}/> {t('common.save', 'SAVE CHANGES')}
            </button>
          )}

          {!isNew && canDelete && (
            <button onClick={handleDelete} className="p-2 hover:bg-red-500/20 text-red-400 rounded transition-colors" title={t('common.delete', 'Delete')}>
              <Trash2 size={16}/>
            </button>
          )}

          {!isNew && (
            <button onClick={() => setShowAttachments(true)} className="relative p-2 hover:bg-white/10 rounded text-blue-200 transition-all" title={t('common.attachments', 'Attachments')}>
              <Paperclip size={16} />
              {attachCount > 0 && (
                <span className="absolute top-1 right-1 bg-red-600 text-[8px] w-3.5 h-3.5 rounded-full flex items-center justify-center font-black animate-pulse shadow-sm">
                  {attachCount}
                </span>
              )}
            </button>
          )}
          
          <div className="w-[1px] h-6 bg-white/10 mx-1" />
          <FontControls />
        </div>
      </div>

      {/* Main Form Fields */}
      <div className="bg-white border-b px-8 py-4 flex items-center gap-12 shadow-sm animate-in fade-in duration-500 z-20">
        <div className="flex flex-col min-w-[200px] group">
          <label className="text-[0.9em] text-red-700 font-black uppercase tracking-widest flex items-center gap-1">
             {t('failure_codes.fields.class', 'Failure Class Code')} *
          </label>
          <input 
            className={`text-xl font-black text-[#1a3353] border-b-2 border-transparent focus:border-orange-500 outline-none uppercase transition-all py-1 bg-transparent ${isRTL ? 'text-right' : 'text-left'}`} 
            value={failureClass.failure_class} 
            onChange={e => setFailureClass({...failureClass, failure_class: e.target.value.toUpperCase()})} 
            placeholder={t('failure_codes.placeholders.class', 'E.G. PUMP-FAIL')}
            disabled={!isNew} // عادةً لا يتم تغيير الـ Primary Key بعد الإنشاء
          />
        </div>
        <div className={`flex flex-col flex-1 border-gray-200 px-8 ${isRTL ? 'border-r' : 'border-l'}`}>
          <label className="text-[0.9em] text-gray-400 font-black uppercase tracking-widest">{t('failure_codes.fields.desc_tech', 'Description / Technical Name')}</label>
          <input 
            className={`text-sm font-bold text-gray-700 border-b-2 border-transparent focus:border-blue-500 outline-none py-1 bg-transparent ${isRTL ? 'text-right' : 'text-left'}`} 
            value={failureClass.description} 
            onChange={e => setFailureClass({...failureClass, description: e.target.value})} 
            placeholder={t('failure_codes.placeholders.desc_tech', 'Provide a detailed description of this failure hierarchy...')}
            disabled={isReadOnly}
          />
        </div>
        <div className={`flex flex-col min-w-[120px] border-gray-200 px-8 ${isRTL ? 'border-r' : 'border-l'}`}>
          <label className="text-[0.9em] text-gray-400 font-black uppercase tracking-widest">{t('common.org', 'Org ID')}</label>
          <input 
            className={`text-sm font-black text-blue-700 outline-none uppercase border-b-2 border-transparent focus:border-blue-400 bg-transparent py-1 ${isRTL ? 'text-right' : 'text-left'}`} 
            value={failureClass.org_id} 
            onChange={e => setFailureClass({...failureClass, org_id: e.target.value})} 
            disabled={isReadOnly}
          />
        </div>
      </div>

      {/* Tables Section */}
      <div className="flex-1 overflow-y-auto p-8 bg-[#f8f9fb] custom-scrollbar">
        <div className="max-w-6xl mx-auto space-y-6">
          <CodeTable 
            title={t('failure_codes.tables.problems', 'Problems (The Issue)')} 
            items={failureClass.problems} 
            type="PROBLEM" 
            updateRow={updateRow} 
            addNewRow={addNewRow} 
            deleteRow={deleteRow}
            t={t} isRTL={isRTL} readOnly={isReadOnly}
          />
          <CodeTable 
            title={t('failure_codes.tables.causes', 'Causes (Root Reason)')} 
            items={failureClass.causes} 
            type="CAUSE" 
            updateRow={updateRow} 
            addNewRow={addNewRow}
            deleteRow={deleteRow}
            t={t} isRTL={isRTL} readOnly={isReadOnly}
          />
          <CodeTable 
            title={t('failure_codes.tables.remedies', 'Remedies (The Action)')} 
            items={failureClass.remedies} 
            type="REMEDY" 
            updateRow={updateRow} 
            addNewRow={addNewRow}
            deleteRow={deleteRow}
            t={t} isRTL={isRTL} readOnly={isReadOnly}
          />
        </div>
      </div>

      {/* Attachments Modal */}
      {showAttachments && !isNew && (
        <AttachmentsModal 
          isOpen={showAttachments}
          onClose={() => { setShowAttachments(false); fetchAttachCount(failureClass.failure_class); }}
          ownertable="FAILURE"
          ownerid={failureClass.failure_class}
        />
      )}
    </div>
  );
};

export default FailureCodesDetails;